// CB:BEGIN operations-control-plane-ui
/**
 * Admin Operations: search (project id, address, homeowner/contractor email, escrow/invoice id) and project timeline.
 * Also lists all active (non-terminal) projects.
 * Goal: answer "What happened on project X?" in under 60 seconds.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useApi } from '../../lib/useApi';

interface SearchProject {
  id: string;
  address: string;
  status: string;
  createdAt: string;
  homeownerId: string | null;
}

/** Terminal statuses – projects in these states are excluded from "active" list */
const TERMINAL_STATUSES = new Set(['CANCELLED', 'COMPLETED', 'CLOSED', 'DISPUTED', 'OUT_OF_SCOPE']);

interface ListProject {
  id: string;
  address: string;
  status: string;
  createdAt: string;
  homeowner?: { firstName?: string; lastName?: string; user?: { email?: string } };
  _count?: { bids: number };
}

interface SearchResult {
  ok: boolean;
  query: string;
  projects: SearchProject[];
  escrows: unknown[];
  invoices: unknown[];
}

interface TimelineItem {
  at: string;
  type: string;
  summary: string;
  payload?: Record<string, unknown>;
}

interface TimelineResult {
  ok: boolean;
  project: { id: string; address: string; status: string };
  timeline: TimelineItem[];
}

interface AdminProjectsResponse {
  projects: ListProject[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

type Section = 'search' | 'active' | 'timeline';

const SECTIONS: { id: Section; label: string; icon: string; summary: string }[] = [
  { id: 'search',   label: 'Project Search',   icon: '🔍', summary: 'Find any project by ID, address, email, or escrow ID.' },
  { id: 'active',   label: 'Active Projects',  icon: '📋', summary: 'All non-terminal projects across the platform.' },
  { id: 'timeline', label: 'Project Timeline', icon: '📅', summary: 'Full audit trail for a selected project.' },
];

const AdminOperationsPage: React.FC = () => {
  const api = useApi();
  const [activeSection, setActiveSection] = useState<Section>('search');
  const sectionRefs = useRef<Record<Section, HTMLElement | null>>({ search: null, active: null, timeline: null });

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<TimelineResult | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [activeProjects, setActiveProjects] = useState<ListProject[]>([]);
  const [activeProjectsLoading, setActiveProjectsLoading] = useState(true);
  const [activeProjectsError, setActiveProjectsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setActiveProjectsError(null);
      setActiveProjectsLoading(true);
      try {
        const data = await api.get<AdminProjectsResponse>('/api/admin/projects?limit=200&page=1');
        if (cancelled) return;
        const active = (data.projects || []).filter((p) => !TERMINAL_STATUSES.has(p.status));
        setActiveProjects(active);
      } catch (e) {
        if (!cancelled) {
          setActiveProjectsError(e instanceof Error ? e.message : 'Failed to load active projects');
          setActiveProjects([]);
        }
      } finally {
        if (!cancelled) setActiveProjectsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const onSearch = useCallback(async () => {
    const q = query.trim();
    if (q.length < 2) { setSearchError('Enter at least 2 characters'); return; }
    setSearchError(null);
    setSearchResults(null);
    setSearchLoading(true);
    try {
      const data = await api.get<SearchResult>(`/api/admin/search?q=${encodeURIComponent(q)}&limit=20`);
      setSearchResults(data);
    } catch (e) {
      setSearchError(e instanceof Error ? e.message : 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  }, [api, query]);

  const loadTimeline = useCallback(async (projectId: string) => {
    setSelectedProjectId(projectId);
    setTimelineError(null);
    setTimeline(null);
    setTimelineLoading(true);
    setActiveSection('timeline');
    setTimeout(() => {
      sectionRefs.current.timeline?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    try {
      const data = await api.get<TimelineResult>(`/api/admin/projects/${projectId}/timeline`);
      setTimeline(data);
    } catch (e) {
      setTimelineError(e instanceof Error ? e.message : 'Failed to load timeline');
    } finally {
      setTimelineLoading(false);
    }
  }, [api]);

  const jumpTo = (id: Section) => {
    setActiveSection(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const sidebarBtnStyle = (id: Section): React.CSSProperties => ({
    width: '100%',
    textAlign: 'left',
    marginBottom: 6,
    padding: '10px 12px',
    borderRadius: 8,
    border: activeSection === id ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
    background: activeSection === id ? '#e0f2fe' : '#fff',
    color: activeSection === id ? '#0c4a6e' : '#334155',
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  });

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#111827' }}>Operations</h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: '#6b7280' }}>
          Find any project, review its timeline, and diagnose issues in under 60 seconds.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '240px minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>

        {/* Sidebar */}
        <aside style={{
          position: 'sticky', top: 80, border: '1px solid #e2e8f0',
          borderRadius: 12, padding: 12, background: '#fff',
        }}>
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => jumpTo(s.id)} style={sidebarBtnStyle(s.id)}>
              <span style={{ fontSize: 16 }}>{s.icon}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.label}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{s.summary}</div>
              </div>
            </button>
          ))}
          {selectedProjectId && (
            <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, background: '#f9fafb', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 2 }}>SELECTED PROJECT</div>
              <div style={{ fontSize: 12, color: '#374151', fontFamily: 'monospace', wordBreak: 'break-all' }}>{selectedProjectId}</div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <main style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Search section */}
          <section
            id="search"
            ref={(n) => { sectionRefs.current.search = n; }}
            style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 20, background: '#fff', scrollMarginTop: 90 }}
            data-testid="admin-operations-page"
          >
            <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#111827' }}>🔍 Project Search</h2>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
              Search by project ID, address, homeowner or contractor email, escrow ID, or invoice ID.
            </p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="text"
                placeholder="Project ID, address, email, escrow ID, invoice ID…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                style={{
                  flex: 1, borderRadius: 8, border: '1px solid #d1d5db',
                  padding: '9px 12px', fontSize: 13, color: '#111827',
                }}
                data-testid="admin-operations-search-input"
              />
              <button
                type="button"
                onClick={onSearch}
                disabled={searchLoading}
                style={{
                  background: '#0369a1', color: 'white', border: 'none', borderRadius: 8,
                  padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: searchLoading ? 'not-allowed' : 'pointer',
                  opacity: searchLoading ? 0.6 : 1,
                }}
                data-testid="admin-operations-search-btn"
              >
                {searchLoading ? 'Searching…' : 'Search'}
              </button>
            </div>
            {searchError && (
              <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 10px' }} role="alert">{searchError}</p>
            )}
            {searchResults && (
              searchResults.projects.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>No projects found for "{searchResults.query}".</p>
              ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                  {searchResults.projects.map((p, i) => (
                    <div
                      key={p.id}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', borderBottom: i < searchResults.projects.length - 1 ? '1px solid #f3f4f6' : 'none',
                        background: i % 2 === 0 ? 'white' : '#fafafa',
                      }}
                    >
                      <div>
                        <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#9ca3af' }}>{p.id}</span>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{p.address}</div>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{p.status}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => loadTimeline(p.id)}
                        style={{
                          background: '#0369a1', color: 'white', border: 'none', borderRadius: 6,
                          padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        }}
                        data-testid={`admin-operations-open-timeline-${p.id}`}
                      >
                        Timeline
                      </button>
                    </div>
                  ))}
                </div>
              )
            )}
          </section>

          {/* Active projects section */}
          <section
            id="active"
            ref={(n) => { sectionRefs.current.active = n; }}
            style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 20, background: '#fff', scrollMarginTop: 90 }}
          >
            <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#111827' }}>
              📋 Active Projects
              {!activeProjectsLoading && (
                <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 400, color: '#6b7280' }}>
                  {activeProjects.length} projects
                </span>
              )}
            </h2>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
              All non-terminal projects. Click Timeline to inspect any project.
            </p>
            {activeProjectsError && (
              <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 10px' }} role="alert">{activeProjectsError}</p>
            )}
            {activeProjectsLoading ? (
              <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</p>
            ) : activeProjects.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>No active projects.</p>
            ) : (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', maxHeight: 380, overflowY: 'auto' }}>
                {activeProjects.map((p, i) => (
                  <div
                    key={p.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderBottom: i < activeProjects.length - 1 ? '1px solid #f3f4f6' : 'none',
                      background: i % 2 === 0 ? 'white' : '#fafafa',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#9ca3af', display: 'block' }}>{p.id}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', display: 'block' }}>{p.address}</span>
                      <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
                        <span style={{ fontSize: 11, color: '#6b7280' }}>{p.status}</span>
                        {p.homeowner?.user?.email && (
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>{p.homeowner.user.email}</span>
                        )}
                        {p._count?.bids !== undefined && (
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>{p._count.bids} bid{p._count.bids !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => loadTimeline(p.id)}
                      style={{
                        background: '#0369a1', color: 'white', border: 'none', borderRadius: 6,
                        padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, marginLeft: 12,
                      }}
                      data-testid={`admin-operations-open-timeline-${p.id}`}
                    >
                      Timeline
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Timeline section */}
          <section
            id="timeline"
            ref={(n) => { sectionRefs.current.timeline = n; }}
            style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 20, background: '#fff', scrollMarginTop: 90 }}
          >
            <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#111827' }}>📅 Project Timeline</h2>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
              Full audit trail — select a project above to load its timeline.
            </p>
            {!selectedProjectId && (
              <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>No project selected. Click "Timeline" on any project above.</p>
            )}
            {timelineError && (
              <p style={{ color: '#dc2626', fontSize: 13, margin: 0 }} role="alert">{timelineError}</p>
            )}
            {timelineLoading && <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading timeline…</p>}
            {timeline && !timelineLoading && (
              <div>
                <div style={{
                  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
                  padding: '10px 14px', marginBottom: 12, fontSize: 13,
                }}>
                  <span style={{ fontWeight: 600, color: '#111827' }}>{timeline.project.address}</span>
                  <span style={{ color: '#6b7280', marginLeft: 10 }}>{timeline.project.status}</span>
                  <span style={{ fontFamily: 'monospace', color: '#9ca3af', marginLeft: 10, fontSize: 11 }}>{timeline.project.id}</span>
                </div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', maxHeight: 440, overflowY: 'auto' }}>
                  {timeline.timeline.length === 0 ? (
                    <div style={{ padding: '24px 14px', color: '#9ca3af', fontSize: 13 }}>No events recorded yet.</div>
                  ) : (
                    timeline.timeline.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex', gap: 14, padding: '10px 14px', fontSize: 13,
                          borderBottom: i < timeline.timeline.length - 1 ? '1px solid #f3f4f6' : 'none',
                          background: i % 2 === 0 ? 'white' : '#fafafa',
                        }}
                      >
                        <span style={{ color: '#9ca3af', flexShrink: 0, minWidth: 130, fontSize: 12 }}>
                          {new Date(item.at).toLocaleString()}
                        </span>
                        <span style={{ fontWeight: 600, color: '#374151', flexShrink: 0, minWidth: 80 }}>{item.type}</span>
                        <span style={{ color: '#6b7280' }}>{item.summary}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </section>

        </main>
      </div>
    </div>
  );
};

export default AdminOperationsPage;
// CB:END operations-control-plane-ui
